/**
 * "Assinar pendentes" do responsável técnico (E164.F2, GUS-113, ADR-0113 §4): os laudos que a
 * assistente já assinou, à espera da assinatura dele, assinados um por vez pelo Assinador SERPRO.
 * Uma conexão só com o programa para a sequência; o primeiro cancelamento ou recusa para tudo e
 * diz em qual laudo parou — o que já foi assinado fica assinado.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Blueprint, Button, CardKicker } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { conectarAssinador, type Assinador } from '@/lib/assinador_serpro';
import { assinaturasPendentes, type PendenteDoResponsavel } from '@/lib/assinatura_icp';
import { formatarDataHora } from '@/lib/trilha_format';

import { assinarComOCertificado, mensagemDaFalha } from './assinaturaIcp';

const t = ptBR.assinaturaIcp.pendentes;

export interface PendentesDoResponsavelProps {
  client: AxiosInstance;
  /** Chamado ao fim da sequência, para a página reler a lista e os indicadores. */
  onAssinados: () => void;
  /** Só para teste: o assinador local dublado. */
  conectar?: () => Promise<Assinador>;
}

async function assinarEmSequencia(
  client: AxiosInstance,
  itens: PendenteDoResponsavel[],
  conectar: () => Promise<Assinador>,
  onAndamento: (i: number) => void,
): Promise<{ assinados: number; parou?: { codigo: string; motivo: string } }> {
  const assinador = await conectar();
  // A mesma conexão para a sequência: cada laudo não fecha o programa (e o PIN pode valer pela sessão).
  const mesmaConexao = (): Promise<Assinador> =>
    Promise.resolve({ assinarPdf: (pdf) => assinador.assinarPdf(pdf), fechar: () => undefined });
  let assinados = 0;
  try {
    for (const [i, item] of itens.entries()) {
      onAndamento(i + 1);
      try {
        await assinarComOCertificado({
          client,
          requisicaoId: item.requisicao_id,
          conectar: mesmaConexao,
        });
      } catch (err) {
        return { assinados, parou: { codigo: item.display_code, motivo: mensagemDaFalha(err) } };
      }
      assinados += 1;
    }
    return { assinados };
  } finally {
    assinador.fechar();
  }
}

function ListaDePendentes({
  itens,
  erro,
}: {
  itens: PendenteDoResponsavel[] | null;
  erro: string | null;
}): JSX.Element {
  if (erro !== null) return <p className="mt-sc-2 text-body-sm text-danger-700">{erro}</p>;
  if (itens === null) return <p className="mt-sc-2 text-body-sm text-fg-muted">{t.carregando}</p>;
  if (itens.length === 0) {
    return (
      <p
        data-testid="pendentes-do-responsavel-vazio"
        className="mt-sc-2 text-body-sm text-fg-muted"
      >
        {t.vazio}
      </p>
    );
  }
  return (
    <ul className="mt-sc-2 flex flex-col gap-1 text-body-sm text-fg">
      {itens.map((item) => (
        <li key={item.laudo_assinatura_id} data-testid="pendente-do-responsavel">
          <span className="font-mono">{item.display_code}</span>
          {' · '}
          {t.primeiraAssinatura
            .replace('{nome}', item.assistente_nome ?? '—')
            .replace('{quando}', formatarDataHora(item.assinada_pelo_assistente_em))}
        </li>
      ))}
    </ul>
  );
}

function Andamento({
  itens,
  andamento,
  desfecho,
}: {
  itens: PendenteDoResponsavel[] | null;
  andamento: number | null;
  desfecho: string | null;
}): JSX.Element {
  const atual = andamento !== null && itens !== null ? itens[andamento - 1] : undefined;
  return (
    <>
      {andamento !== null && itens !== null ? (
        <p role="status" data-testid="pendentes-andamento" className="mt-sc-2 text-body-sm">
          {t.andamento
            .replace('{i}', String(andamento))
            .replace('{n}', String(itens.length))
            .replace('{codigo}', atual?.display_code ?? '')}
        </p>
      ) : null}
      {desfecho !== null ? (
        <p role="status" data-testid="pendentes-desfecho" className="mt-sc-2 text-body-sm text-fg">
          {desfecho}
        </p>
      ) : null}
    </>
  );
}

function textoDoDesfecho(r: { assinados: number; parou?: { codigo: string; motivo: string } }) {
  const feito = t.concluido.replace('{n}', String(r.assinados));
  if (r.parou === undefined) return feito;
  const parou = t.interrompido
    .replace('{codigo}', r.parou.codigo)
    .replace('{motivo}', r.parou.motivo);
  return `${parou} ${feito}`;
}

export function PendentesDoResponsavel(props: PendentesDoResponsavelProps): JSX.Element {
  const { client } = props;
  const [itens, setItens] = useState<PendenteDoResponsavel[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [andamento, setAndamento] = useState<number | null>(null);
  const [desfecho, setDesfecho] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setItens(await assinaturasPendentes(client));
    } catch {
      setErro(t.erro);
    }
  }, [client]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function assinarTodos(lista: PendenteDoResponsavel[]): Promise<void> {
    setDesfecho(null);
    try {
      const r = await assinarEmSequencia(
        client,
        lista,
        props.conectar ?? conectarAssinador,
        setAndamento,
      );
      setDesfecho(textoDoDesfecho(r));
    } catch (err) {
      setDesfecho(mensagemDaFalha(err));
    } finally {
      setAndamento(null);
      await carregar();
      props.onAssinados();
    }
  }

  return (
    <Blueprint as="section" data-testid="pendentes-do-responsavel" className="p-sc-4">
      <CardKicker className="block">{ptBR.assinaturaIcp.assinarComoResponsavel}</CardKicker>
      <h2 className="mt-1 text-heading-sm text-fg">{t.titulo}</h2>
      <p className="mt-1 text-body-sm text-fg-muted">{t.texto}</p>
      <ListaDePendentes itens={itens} erro={erro} />
      <Andamento itens={itens} andamento={andamento} desfecho={desfecho} />
      {itens !== null && itens.length > 0 ? (
        <Button
          type="button"
          className="mt-sc-3"
          data-testid="assinar-pendentes"
          disabled={andamento !== null}
          onClick={() => void assinarTodos(itens)}
        >
          {t.assinarTodos.replace('{n}', String(itens.length))}
        </Button>
      ) : null}
    </Blueprint>
  );
}
